import React from "react";
import styled from "styled-components";
import { Box } from "../Box";
import { useEvent } from "../hooks/useEvent";
import { useIsFocused } from "../hooks/useIsFocused";
import { Icon } from "../Icon";
import { style } from "../style";
import { LooseInteractionButton, LooseTextInput } from "../__Internal__";

const OuterContainer = styled.div`
  display: inline-block;
`;

const InnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  height: 44px;
  width: 44px;
  position: relative;
`;

const InputWidthWrapper = styled.div`
  height: 48px;
  position: relative;
`;

const InputWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

const WidthTracker = styled.div`
  padding: 0 ${style.vars.space["3"]};
  margin: 0 ${style.vars.space["2"]};
  position: relative;
`;

type Props = {
  /**
   * The name of the paginator.
   * @default undefined
   */
  name?: string;

  /**
   * The current page number.
   */
  page: number;
  /**
   * The maximum number of pages.
   */
  max: number;
  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;

  onChange(next: number): void;

  /**
   * Testing utility tag.
   * @default "paginator"
   */
  "data-testid"?: string;
};

export const PaginatorBase: React.FC<Props> = (props) => {
  const { name, page, max, disabled = false } = props;
  const testId = props["data-testid"] ?? "paginator";

  const inputRef = React.useRef<HTMLInputElement>(null);
  const isInputFocused = useIsFocused(inputRef);
  const [innerPage, setInnerPage] = React.useState(() => page.toString());
  const isMin = parseInt(innerPage, 10) === 1;
  const isMax = parseInt(innerPage, 10) === max;

  React.useEffect(() => {
    if (!isInputFocused && innerPage !== page.toString()) {
      setInnerPage(page.toString());
    }
  }, [isInputFocused, page, innerPage]);

  const handleChange = useEvent<React.ChangeEventHandler<HTMLInputElement>>((ev) => {
    /**
     * If we parse the value as a number, then apply the constraints on it, otherwise,
     * just use whatever the user typed.
     */
    const next = ev.target.valueAsNumber;

    if (isNaN(next)) {
      setInnerPage(ev.target.value);
    } else if (next < 1) {
      setInnerPage("1");
    } else if (next > max) {
      setInnerPage(max.toString());
    } else {
      setInnerPage(next.toString());
    }
  });

  const handleBlur = useEvent(() => {
    const next = parseInt(innerPage, 10);

    if (isNaN(next)) {
      setInnerPage(page.toString());
    }

    if (next !== page && next >= 1 && next <= max) {
      props.onChange(next);
    }
  });

  const handleKeyDown = useEvent<React.KeyboardEventHandler<HTMLInputElement>>((ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      handleBlur();
    }
  });

  const handleDecrement = useEvent((ev: React.MouseEvent) => {
    ev.preventDefault();
    const next = page - 1;

    if (next >= 1) {
      props.onChange(next);
    }
  });

  const handleIncrement = useEvent((ev: React.MouseEvent) => {
    ev.preventDefault();
    const next = page + 1;

    if (next <= max) {
      props.onChange(next);
    }
  });

  return (
    <OuterContainer>
      <InnerContainer>
        <ButtonWrapper>
          <LooseInteractionButton
            data-testid={`${testId}/decrement`}
            onClick={handleDecrement}
            disabled={disabled || isMin}
            borderTop={false}
            borderRight={false}
            borderBottom={false}
            borderLeft={false}
            borderRadiusBottomLeft="base"
            borderRadiusBottomRight="base"
            borderRadiusTopLeft="base"
            borderRadiusTopRight="base"
          >
            <Icon icon="arrow-left" />
          </LooseInteractionButton>
        </ButtonWrapper>
        <Box ml={1} />
        <span>Page</span>
        <Box ml={3} />
        <InputWidthWrapper>
          <WidthTracker>{max}</WidthTracker>
          <InputWrapper>
            <LooseTextInput
              data-testid={testId}
              ref={inputRef}
              name={name}
              id={name}
              type="number"
              value={innerPage}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              min={1}
              max={max}
              UNSAFE_center
            />
          </InputWrapper>
        </InputWidthWrapper>
        <Box ml={3} />
        <span>of</span>
        <Box ml={2} />
        <span>{max}</span>
        <Box ml={1} />
        <ButtonWrapper>
          <LooseInteractionButton
            data-testid={`${testId}/increment`}
            onClick={handleIncrement}
            disabled={disabled || isMax}
            borderTop={false}
            borderRight={false}
            borderBottom={false}
            borderLeft={false}
            borderRadiusBottomLeft="base"
            borderRadiusBottomRight="base"
            borderRadiusTopLeft="base"
            borderRadiusTopRight="base"
          >
            <Icon icon="arrow-right" />
          </LooseInteractionButton>
        </ButtonWrapper>
      </InnerContainer>
    </OuterContainer>
  );
};
